import React, { useState, useEffect, useCallback } from "react";
import { StaticQuery, graphql } from "gatsby";
import { useIsClient, useWindowSize } from "../../hooks";
import { GroupLogos } from "../GroupLogos";
import { Hamburger } from "../Hamburger";
import {
  GlobalHeaderStateProvider,
  useHeaderState,
} from "../Header/HeaderContext";
import { Logo } from "../Logo";
import { ContactUsToday } from "../ContactUsToday";
import { HeaderNav } from "../HeaderNav";
import { SearchBar } from "./SearchBar";
import "./Header.scss";

const HeaderComponent = ({
  data: {
    primaryNavigation: { items: navItems },
    siteSettings: { options: siteOptions },
    metaData,
    wordpressWpSettings: { baseUrl },
  },
}) => {
  // get the state of the menu whether it is open or closed
  const {
    dispatch,
    state: { menuOpen },
  } = useHeaderState();

  const isClient = useIsClient();

  // closes the mobile menu if esc key is pressed
  const handleEscKey = useCallback(
    (event) =>
      event.keyCode === 27 &&
      menuOpen &&
      dispatch({ type: "setMenuOpen", data: false }),
    [menuOpen, dispatch]
  );

  const size = useWindowSize();

  // closes the mobile menu if esc key is pressed
  useEffect(() => {
    if (isClient) {
      document.addEventListener("keydown", handleEscKey, false);
      return () => {
        document.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [handleEscKey, isClient]);

  // Check for window resize to see if menu needs to 'close' for desktop
  useEffect(() => {
    if (isClient && size.width > 1080 && menuOpen) {
      dispatch({
        type: "setMenuOpen",
        data: false,
      });
    }
  }, [isClient, size]);

  // handles whether there is a child menu item open on mobile
  const [openChild, setOpenChild] = useState(null);

  const onArrowClick = (newOpenChild) => {
    if (openChild === newOpenChild) return setOpenChild(null);
    return setOpenChild(newOpenChild);
  };

  return (
    <header className="header">
      <GroupLogos />
      <div className="header-contact layout">
        <div className="header-contact__wrapper">
          {Object.keys(siteOptions.contact_numbers).length > 0 &&
            siteOptions.contact_numbers?.map((location, index) => {
              return (
                <span key={index} className="header-contact__details">
                  <span
                    className="header-contact__details__region"
                    dangerouslySetInnerHTML={{ __html: location.region }}
                  />
                  <a
                    href={`tel:${location?.number.replace(/[^A-Z0-9]/gi, "")}`}
                    className="header-contact__details__phone"
                    aria-label="Call Now"
                  >
                    {location?.number}
                  </a>
                </span>
              );
            })}
        </div>
      </div>
      <div
        className={`header-main layout-wide ${
          menuOpen ? "header-main--open" : ""
        }`}
      >
        <div className="header-main__wrapper">
          <Hamburger />
          <Logo />
          <div
            className={`header-main__navigation-wrapper ${
              menuOpen ? "header-main__navigation-wrapper--open" : ""
            } ${
              openChild !== null
                ? "header-main__navigation-wrapper--open-child"
                : ""
            }`}
          >
            <HeaderNav
              navItems={navItems}
              onArrowClick={onArrowClick}
              openChild={openChild}
              phoneNumbers={siteOptions.locations}
            />
          </div>
          <ContactUsToday className="header-main__contact-btn" />
          <SearchBar />
        </div>
      </div>
    </header>
  );
};

export function Header() {
  return (
    <StaticQuery
      query={graphql`
        query {
          abovePrimaryNavigation: wordpressWpApiMenusMenusItems(
            slug: { eq: "above-primary-navigation" }
          ) {
            items {
              title
              url
              classes
              object_id
              slug: object_slug
              target
              children: wordpress_children {
                title
                url
                classes
                object_id
                slug: object_slug
                target
              }
            }
          }
          primaryNavigation: wordpressWpApiMenusMenusItems(
            slug: { eq: "primary-navigation" }
          ) {
            items {
              title
              url
              classes
              slug: object_slug
              object_id
              target
              children: wordpress_children {
                title
                url
                classes
                object_id
                slug: object_slug
                target
                grandchildren: wordpress_children {
                  title
                  url
                  classes
                  object_id
                  grandslug: object_slug
                  target
                }
              }
            }
          }
          siteSettings: wordpressAcfOptions(options: {}) {
            options {
              locations {
                phone
                addressRegion
              }
              contact_numbers {
                number
                region
              }
            }
          }
          wordpressWpSettings {
            siteUrl
          }
        }
      `}
      render={(data) => (
        <GlobalHeaderStateProvider>
          <HeaderComponent data={data} />
        </GlobalHeaderStateProvider>
      )}
    />
  );
}
